/**
 * This is deprecated in favor of PlacesAutocompleteAddressTextFieldV2
 * which uses the new Google Places API (New).
 * Holding here till the new version has spent some time in production
 * incase we need to roll back.
*/

import React, { useRef, useEffect } from "react";
import { TextFieldGroup } from "components/Form";

const GOOGLE_PLACES_AUTOCOMPLETE_OPTIONS = {
  fields: ["formatted_address", "address_component"],
  types: ["address"],
};

/**
 * AddressComponentsDictPayload
 * @typedef {Object} AddressComponentsDictPayload
 * @prop {String} long_name
 * @prop {String} short_name
 */

/**
 * AddressComponentsDict
 * @typedef {Object.<string, AddressComponentsDictPayload>} AddressComponentsDict
 */

/**
 * formatAddressComponentsToDict
 * @param {Array.<google.maps.GeocoderAddressComponent>} address_components - see Google Maps Api for typescript def
 * @returns {AddressComponentsDict}
 * @description
 * Formats the address_components data from Google Places api into a more manageable shape
 */
export const formatAddressComponentsToDict = (address_components = []) => {
  const results = {};
  address_components.forEach((ac) => {
    const { types = [], long_name, short_name } = ac;
    types.forEach((t) => (results[t] = { long_name, short_name }));
  });
  return results;
};

/**
 * getTiliaAddressFromPlacesDict
 * @param {AddressComponentsDict} address_components_dict
 */
export const getTiliaAddressFromPlacesDict = (address_components_dict = {}) => {
  const d = address_components_dict;
  const address = {
    street: `${d.street_number?.long_name || ""}${
      d.route?.short_name ? ` ${d.route?.short_name}` : ""
    }`,
    street2: `${d.subpremise?.long_name || ""}`,
    city: `${
      d.locality?.long_name ||
      d.sublocality?.long_name ||
      d.sublocality_level_1?.long_name ||
      d.sublocality_level_2?.long_name ||
      d.sublocality_level_3?.long_name ||
      d.sublocality_level_4?.long_name ||
      ""
    }`,
    state: `${
      d.administrative_area_level_1?.short_name ||
      d.administrative_area_level_2?.short_name ||
      d.administrative_area_level_3?.short_name ||
      d.administrative_area_level_4?.short_name ||
      d.administrative_area_level_5?.short_name ||
      ""
    }`,
    zip: `${d.postal_code?.long_name || ""}${
      d.postal_code_suffix?.long_name ? `-${d.postal_code_suffix?.long_name}` : ""
    }`,
    country: `${d.country?.short_name || ""}`,
  };
  return address;
};

/**
 * PlacesAutocompleteAddressTextField
 * @description
 * Adds Google Places Autcomplete API support to TextField component.
 * This is just an additive feature.  When available (google maps lib loaded on window),
 * this will allow address autocomplete where a user can select from a number of matching
 * items based on the input.  When not available, this will function as a normal text field.
 * As such, this field should only be used for address purposes.
 * @todo
 * We may want to open up 'options' support in this component for filtering by a country.
 */
const PlacesAutocompleteAddressTextField = ({ onUserSelection, country, isEditState, ...rest }) => {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const onUserSelectionRef = useRef(onUserSelection);
  /**
   * We need to capture component callback to imperitively manage the callback reference along with
   * the rest of the of the Google Places references.
   */
  useEffect(() => {
    onUserSelectionRef.current = onUserSelection;
  }, [onUserSelection]);

  useEffect(() => {
    // as set up, the Google Places Autocomplete API is only additave.
    // if available, provides address autocomplete functionality for the user,
    // if not available, then no need to setup or return cleanup.
    // Text field will work as a normal text input
    if (!window.google?.maps?.places?.Autocomplete) {
      return;
    }

    autoCompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      GOOGLE_PLACES_AUTOCOMPLETE_OPTIONS
    );

    const onPlaceChanged = async () => {
      try {
        const place = await autoCompleteRef.current.getPlace();
        const { address_components, formatted_address } = place;
        if (!address_components && !formatted_address) {
          /**
           * it's possible for this callback to fire without a user making a Places selection (say, if user clicks enter without making selection),
           * in those cases it would fire with only info about what string is in the input field.
           * In these cases, for now, we are going to ignore the callback.
           */
          return;
        }
        const address_components_dict = formatAddressComponentsToDict(address_components);
        const tilia_address = getTiliaAddressFromPlacesDict(address_components_dict);
        if (onUserSelectionRef.current) {
          onUserSelectionRef.current({
            formatted_address,
            address_components,
            address_components_dict,
            tilia_address,
          });
        }
      } catch (err) {
        console.error("handlePlaceSelection error: ", err);
      }
    };

    autoCompleteRef.current.addListener("place_changed", onPlaceChanged);

    return () => {
      autoCompleteRef.current.unbindAll();
    };
  }, []);

  useEffect(() => {
    if (country && autoCompleteRef.current) {
      autoCompleteRef.current.setComponentRestrictions({
        country: [country],
      });
    }
  }, [country]);

  return <TextFieldGroup {...rest} ref={inputRef} className="google-places-autocomplete-field" />;
};

export default PlacesAutocompleteAddressTextField;
